<template>
  <div class="ranking-report">
    <PageSpinner :onloader="loading ? 'flex' : 'none'" />
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <router-link to="/dashboard" class="text-white">Dashboard</router-link>
      <router-link to="/school/reports-and-analytics" class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Reports and Analytics</router-link
      >
      <span class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Class Performing Ranking</span
      >
    </base-header>

    <main class="container-fluid mt--7 ranking-shell">
      <header class="page-heading">
        <div>
          <span class="eyebrow">ACADEMIC PERFORMANCE</span>
          <h1>Class performance ranking</h1>
          <p>
            Compare student achievement, identify strengths, and find where
            extra support can help.
          </p>
        </div>
        <div class="heading-actions">
          <span class="mode-pill" :class="mockMode ? 'demo-pill' : 'live-pill'"
            ><i class="fa" :class="mockMode ? 'fa-flask' : 'fa-database'"></i
            >{{
              mockMode ? "Demonstration data" : "Live academic records"
            }}</span
          ><button
            class="btn btn-sm btn-outline-primary"
            type="button"
            :disabled="loading"
            @click="toggleMode"
          >
            {{ mockMode ? "Return to live data" : "Preview demonstration" }}
          </button>
        </div>
      </header>

      <section class="filter-panel card border-0 shadow-sm mb-4">
        <div class="card-body">
          <div class="section-heading">
            <div>
              <h2>Academic scope</h2>
              <p>
                Choose the term and group to compare students on the same set of
                subjects.
              </p>
            </div>
          </div>
          <div class="filter-grid">
            <div class="form-group">
              <label for="term"
                >Registered term <span class="required">*</span
                ><span v-if="listLoading.terms" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="term"
                v-model="filters.RegisteredTermId"
                class="form-control"
                :disabled="listLoading.terms"
              >
                <option value="">
                  {{
                    listLoading.terms
                      ? "Loading terms…"
                      : "Select session and term"
                  }}
                </option>
                <option
                  v-for="term in terms"
                  :key="term.registeredTermId"
                  :value="String(term.registeredTermId)"
                >
                  {{ term.schSession }} · {{ term.schTerm }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="class"
                >Class
                <span v-if="listLoading.classes" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="class"
                v-model="filters.ClassId"
                class="form-control"
                :disabled="listLoading.classes"
                @change="onClassChange"
              >
                <option value="">
                  {{
                    listLoading.classes
                      ? "Loading classes…"
                      : "All assigned classes"
                  }}
                </option>
                <option
                  v-for="item in classes"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.Sch_Class }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="option"
                >Option
                <span v-if="listLoading.options" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="option"
                v-model="filters.OptionId"
                class="form-control"
                :disabled="!filters.ClassId || listLoading.options"
                @change="onOptionChange"
              >
                <option value="">
                  {{ listLoading.options ? "Loading options…" : "All options" }}
                </option>
                <option
                  v-for="item in options"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.ClassOption }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="section"
                >Section (Arm) <span class="required">*</span
                ><span v-if="listLoading.sections" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="section"
                v-model="filters.SectionId"
                class="form-control"
                :disabled="
                  !filters.OptionId ||
                  (!mockMode && !filters.RegisteredTermId) ||
                  listLoading.sections
                "
                @change="onSectionChange"
              >
                <option value="">
                  {{ mockMode ? "All sections" : "Select a section" }}
                </option>
                <option
                  v-for="item in sections"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.SectionName }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="student"
                >Student
                <span v-if="listLoading.students" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="student"
                v-model="filters.StudentId"
                class="form-control"
                :disabled="!students.length || listLoading.students"
              >
                <option value="">
                  {{
                    listLoading.students ? "Loading students…" : "All students"
                  }}
                </option>
                <option
                  v-for="item in students"
                  :key="studentId(item)"
                  :value="String(studentId(item))"
                >
                  {{ studentName(item) }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="assessment">Assessment</label
              ><select
                id="assessment"
                v-model="filters.AssessmentType"
                class="form-control"
              >
                <option value="full">Full term</option>
                <option value="mid">Mid-term</option>
              </select>
            </div>
            <div class="form-group">
              <label for="subject"
                >Subject
                <span v-if="listLoading.subjects" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="subject"
                v-model="filters.SubjectId"
                class="form-control"
                :disabled="!subjects.length || listLoading.subjects"
              >
                <option value="">
                  {{
                    listLoading.subjects
                      ? "Loading subjects…"
                      : "All assigned subjects"
                  }}
                </option>
                <option
                  v-for="item in subjects"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.SchSubject }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="gradeCategory">Grading scale</label
              ><select
                id="gradeCategory"
                v-model="filters.GradeCategoryId"
                class="form-control"
                :disabled="listLoading.gradeScales"
                @change="loadGradeScale"
              >
                <option value="">
                  {{
                    listLoading.gradeScales
                      ? "Loading scale…"
                      : "Choose school scale"
                  }}
                </option>
                <option
                  v-for="item in gradeCategories"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.CategoryName }}
                </option>
              </select>
            </div>
            <div class="filter-actions">
              <button
                class="btn btn-primary"
                type="button"
                :disabled="loading || !filters.RegisteredTermId"
                @click="applyFilters"
              >
                <i class="fa fa-filter mr-1"></i>Apply filters</button
              ><button
                class="btn btn-outline-secondary"
                type="button"
                :disabled="loading"
                @click="clearFilters"
              >
                Clear filters
              </button>
            </div>
          </div>
          <p class="filter-note">
            <i class="fa fa-circle-info mr-1"></i
            >{{
              mockMode
                ? "Demonstration records support all filters, ties, missing scores, and historical comparisons."
                : "Live marks are available for one selected section and its assigned subjects. Rankings use total marks ÷ total obtainable marks; incomplete students are shown but excluded from ranks."
            }}
          </p>
          <div v-if="activeFilters.length" class="active-filters">
            <strong>Applied:</strong
            ><span
              v-for="item in activeFilters"
              :key="item"
              class="filter-chip"
              >{{ item }}</span
            >
          </div>
        </div>
      </section>

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        <i class="fa fa-circle-exclamation mr-2"></i>{{ errorMessage }}
      </div>
      <div v-if="mockMode && hasGenerated" class="demo-banner">
        <i class="fa fa-flask"></i
        ><span
          ><strong>Demonstration data.</strong> Illustrative student results
          only. These records are not official school results.</span
        >
      </div>

      <div v-if="hasGenerated && !loading">
        <section
          class="kpi-grid mb-4"
          aria-label="Academic performance summary"
        >
          <article
            v-for="metric in metrics"
            :key="metric.label"
            class="kpi-card"
            :class="metric.tone"
            :title="metric.help"
          >
            <span class="kpi-icon"
              ><i class="fa" :class="metric.icon"></i
            ></span>
            <div>
              <small>{{ metric.label }}</small
              ><strong>{{ metric.value }}</strong
              ><span class="kpi-note">{{ metric.note }}</span>
            </div>
          </article>
        </section>

        <section class="analysis-grid mb-4">
          <article
            class="analysis-card card border-0 shadow-sm score-distribution"
          >
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Student score distribution</h2>
                  <p>
                    {{
                      gradeBands.length
                        ? "Students grouped by overall percentage"
                        : "Choose a school grading scale to show configured bands"
                    }}
                  </p>
                </div>
              </div>
              <div v-if="gradeBands.length" class="distribution-bars">
                <div
                  v-for="band in gradeBands"
                  :key="band.label"
                  class="distribution-row"
                >
                  <span>{{ band.label }}</span>
                  <div class="distribution-track">
                    <i
                      :style="{
                        width: `${barWidth(band.count, maxBandCount)}%`,
                        background: band.color,
                      }"
                    ></i>
                  </div>
                  <strong>{{ band.count }}</strong>
                </div>
              </div>
              <div v-else class="chart-empty">
                No grading bands are configured for this report.
              </div>
            </div>
          </article>
          <article
            class="analysis-card card border-0 shadow-sm subject-analysis"
          >
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Subject performance</h2>
                  <p>Average mark percentage across students with results.</p>
                </div>
              </div>
              <div v-if="subjectSummary.length" class="subject-bars">
                <div
                  v-for="subject in subjectSummary"
                  :key="subject.id"
                  class="subject-row"
                >
                  <span :title="subject.name">{{ subject.name }}</span>
                  <div class="subject-track">
                    <i
                      :class="subject.average < passMark ? 'weak-subject' : ''"
                      :style="{ width: `${subject.average}%` }"
                    ></i>
                  </div>
                  <strong>{{ subject.average }}%</strong
                  ><small>{{ subject.students }} students</small>
                </div>
              </div>
              <div v-else class="chart-empty">
                No subject results are available.
              </div>
            </div>
          </article>
          <article class="analysis-card card border-0 shadow-sm top-performers">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Top performers</h2>
                  <p>
                    Top {{ topStudents.length }} ranked students in this scope.
                  </p>
                </div>
              </div>
              <div v-if="topStudents.length" class="top-list">
                <div
                  v-for="student in topStudents"
                  :key="student.id"
                  class="top-row"
                >
                  <span class="rank-medal">{{ student.rank }}</span
                  ><span class="top-student"
                    ><strong>{{ student.name }}</strong
                    ><small>{{ student.admission }}</small></span
                  >
                  <div class="mini-track">
                    <i :style="{ width: `${student.percentage}%` }"></i>
                  </div>
                  <b>{{ student.percentage }}%</b>
                </div>
              </div>
              <div v-else class="chart-empty">No complete results to rank.</div>
            </div>
          </article>
          <article
            class="analysis-card card border-0 shadow-sm group-comparison"
          >
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>{{ comparisonTitle }}</h2>
                  <p>Average percentage for complete results in each group.</p>
                </div>
              </div>
              <div v-if="groupComparison.length > 1" class="comparison-list">
                <div
                  v-for="group in groupComparison"
                  :key="group.key"
                  class="comparison-row"
                >
                  <span :title="group.label">{{ group.label }}</span>
                  <div class="subject-track">
                    <i :style="{ width: `${group.average}%` }"></i>
                  </div>
                  <strong>{{ group.average }}%</strong>
                </div>
              </div>
              <div v-else class="chart-empty">
                Select a wider demonstration scope to compare multiple groups.
              </div>
            </div>
          </article>
          <article class="analysis-card card border-0 shadow-sm grade-chart">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Grade distribution</h2>
                  <p>One overall grade per complete student.</p>
                </div>
              </div>
              <div v-if="gradeDistribution.length" class="grade-layout">
                <div
                  class="grade-donut"
                  :style="gradeDonutStyle"
                  role="img"
                  aria-label="Grade distribution"
                >
                  <div>
                    <strong>{{ completeStudents.length }}</strong
                    ><small>students</small>
                  </div>
                </div>
                <ul class="grade-legend">
                  <li v-for="grade in gradeDistribution" :key="grade.label">
                    <i :style="{ background: grade.color }"></i
                    ><span
                      >{{ grade.label
                      }}<small
                        >{{ grade.count }} · {{ grade.percent }}%</small
                      ></span
                    >
                  </li>
                </ul>
              </div>
              <div v-else class="chart-empty">
                Select a school grading scale to show grades.
              </div>
            </div>
          </article>
          <article class="analysis-card card border-0 shadow-sm pass-chart">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Pass and support</h2>
                  <p>
                    {{
                      passMark !== null
                        ? `Pass boundary: ${passMark}%`
                        : "Pass boundary is not configured"
                    }}
                  </p>
                </div>
              </div>
              <div v-if="passMark !== null" class="pass-summary">
                <div class="pass-ring" :style="passRingStyle">
                  <strong>{{ passRate }}%</strong>
                </div>
                <div class="pass-stats">
                  <span
                    ><i class="pass-dot"></i>Passing
                    <b>{{ passedStudents.length }}</b></span
                  ><span
                    ><i class="fail-dot"></i>Below pass mark
                    <b>{{ failedStudents.length }}</b></span
                  ><span
                    ><i class="support-dot"></i>Needs support
                    <b>{{ supportStudents.length }}</b></span
                  >
                </div>
              </div>
              <div v-else class="chart-empty">
                Select the school grading scale to determine pass and support
                counts.
              </div>
            </div>
          </article>
          <article class="analysis-card card border-0 shadow-sm history-chart">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Performance trend</h2>
                  <p>
                    {{
                      mockMode
                        ? "Illustrative previous terms"
                        : "Historical mark comparison"
                    }}
                  </p>
                </div>
              </div>
              <div v-if="trendData.length" class="trend-wrap">
                <svg
                  viewBox="0 0 500 185"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label="Average score by term"
                >
                  <g v-for="tick in [0, 25, 50, 75, 100]" :key="tick">
                    <line
                      x1="38"
                      :y1="trendY(tick)"
                      x2="490"
                      :y2="trendY(tick)"
                      class="chart-gridline"
                    />
                    <text
                      x="30"
                      :y="trendY(tick) + 4"
                      text-anchor="end"
                      class="chart-axis"
                    >
                      {{ tick }}
                    </text>
                  </g>
                  <polyline
                    :points="trendLine"
                    fill="none"
                    stroke="#3d80dc"
                    stroke-width="3"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                  <g v-for="point in trendData" :key="point.label">
                    <circle
                      :cx="point.x"
                      :cy="trendY(point.value)"
                      r="4"
                      fill="#3d80dc"
                    >
                      <title>{{ point.label }}: {{ point.value }}%</title>
                    </circle>
                    <text
                      :x="point.x"
                      y="178"
                      text-anchor="middle"
                      class="chart-axis"
                    >
                      {{ point.label }}
                    </text>
                  </g>
                </svg>
              </div>
              <div v-else class="chart-empty">
                Historical results are not provided by the live mark-record
                service.
              </div>
            </div>
          </article>
        </section>

        <section
          v-if="selectedStudent"
          class="student-detail card border-0 shadow-sm mb-4"
        >
          <div class="card-body">
            <div class="section-heading">
              <div>
                <h2>{{ selectedStudent.name }}</h2>
                <p>
                  {{ selectedStudent.admission }} ·
                  {{ selectedStudent.className }} ·
                  {{ selectedStudent.optionName }} ·
                  {{ selectedStudent.sectionName }}
                </p>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                @click="closeStudent"
              >
                Close details
              </button>
            </div>
            <div class="detail-stats">
              <div>
                <small>Rank</small
                ><strong>{{ selectedStudent.rank || "Not ranked" }}</strong>
              </div>
              <div>
                <small>Average</small
                ><strong>{{ selectedStudent.percentage }}%</strong>
              </div>
              <div>
                <small>Marks</small
                ><strong
                  >{{ formatNumber(selectedStudent.totalObtained) }} /
                  {{ formatNumber(selectedStudent.totalMaximum) }}</strong
                >
              </div>
              <div>
                <small>Subjects with results</small
                ><strong
                  >{{ selectedStudent.resultsCount }} /
                  {{ selectedStudent.subjectCount }}</strong
                >
              </div>
              <div>
                <small>Subjects passed / failed</small
                ><strong
                  >{{ selectedStudent.passed }} /
                  {{ selectedStudent.failed }}</strong
                >
              </div>
              <div>
                <small>Grade</small
                ><strong>{{ selectedStudent.grade || "—" }}</strong>
              </div>
            </div>
            <div class="subject-detail-table">
              <h3>Subject results</h3>
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>CA</th>
                      <th>Exam</th>
                      <th>Obtained</th>
                      <th>Maximum</th>
                      <th>Percentage</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="subject in selectedStudent.subjectResults"
                      :key="subject.id"
                    >
                      <td>{{ subject.name }}</td>
                      <td>{{ formatNumber(subject.ca) }}</td>
                      <td>{{ formatNumber(subject.exam) }}</td>
                      <td>{{ formatNumber(subject.obtained) }}</td>
                      <td>{{ formatNumber(subject.maximum) }}</td>
                      <td>
                        {{
                          subject.percentage === null
                            ? "—"
                            : `${subject.percentage}%`
                        }}
                      </td>
                      <td>{{ subject.grade || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <p class="print-context">
          Class Performance Ranking · {{ activeFilters.join(" · ") }}
        </p>
        <section class="ranking-table card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-heading">
              <div>
                <h2>Student ranking</h2>
                <p>
                  {{ filteredRows.length }} students · rank scope:
                  {{ scopeLabel }} · ties use competition ranking (1, 2, 2, 4)
                </p>
              </div>
              <div class="table-tools">
                <label class="search-box"
                  ><i class="fa fa-search"></i
                  ><input
                    v-model.trim="search"
                    type="search"
                    placeholder="Search name or admission no."
                    aria-label="Search students"
                    @input="page = 1" /></label
                ><button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="!filteredRows.length"
                  @click="exportRanking"
                >
                  <i class="fa fa-file-excel mr-1"></i>Export Excel</button
                ><button
                  class="btn btn-outline-secondary btn-sm"
                  type="button"
                  :disabled="!filteredRows.length"
                  @click="printRanking"
                >
                  <i class="fa fa-print mr-1"></i>Print
                </button>
              </div>
            </div>
            <div v-if="filteredRows.length" class="table-responsive">
              <table class="table ranking-data mb-0">
                <thead>
                  <tr>
                    <th
                      v-for="column in columns"
                      :key="column.key"
                      :class="{ sortable: true }"
                      @click="sortBy(column.key)"
                    >
                      {{ column.label }}
                      <i class="fa ml-1" :class="sortIcon(column.key)"></i>
                    </th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in pagedRows"
                    :key="row.id"
                    :class="{
                      'top-row-highlight': row.rank && row.rank <= 3,
                      'incomplete-row': !row.complete,
                      'support-row': isAtRisk(row),
                    }"
                  >
                    <td>
                      <span
                        v-if="row.rank"
                        class="rank-badge"
                        :class="{
                          'rank-first': row.rank === 1,
                          'rank-second': row.rank === 2,
                          'rank-third': row.rank === 3,
                        }"
                        >{{ row.rank }}</span
                      ><span v-else class="unranked">—</span>
                    </td>
                    <td>{{ row.admission }}</td>
                    <td>
                      <strong>{{ row.name }}</strong
                      ><small v-if="!row.complete" class="incomplete-note"
                        >Incomplete results</small
                      >
                    </td>
                    <td>{{ row.className }}</td>
                    <td>{{ row.optionName }}</td>
                    <td>{{ row.sectionName }}</td>
                    <td>{{ row.subjectCount }}</td>
                    <td>{{ row.resultsCount }}</td>
                    <td>{{ formatNumber(row.totalObtained) }}</td>
                    <td>{{ formatNumber(row.totalMaximum) }}</td>
                    <td>
                      <strong>{{
                        row.complete ? `${row.percentage}%` : "—"
                      }}</strong>
                    </td>
                    <td>{{ row.grade || "—" }}</td>
                    <td>{{ passMark === null ? "—" : row.passed }}</td>
                    <td>{{ passMark === null ? "—" : row.failed }}</td>
                    <td>
                      <span class="status-badge" :class="statusClass(row)">{{
                        statusLabel(row)
                      }}</span>
                    </td>
                    <td>
                      <button
                        class="btn btn-link btn-sm p-0"
                        type="button"
                        @click="openStudent(row)"
                      >
                        View details
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <i class="fa fa-chart-simple"></i>
              <h3>No academic results</h3>
              <p>
                {{
                  search
                    ? "Try another student name or admission number."
                    : "Choose a section and apply filters to load student results."
                }}
              </p>
            </div>
            <div v-if="filteredRows.length" class="pagination-row">
              <span
                >Showing {{ pageStart + 1 }}–{{
                  Math.min(pageStart + pageSize, filteredRows.length)
                }}
                of {{ filteredRows.length }}</span
              >
              <div class="page-controls">
                <label
                  >Rows
                  <select
                    v-model.number="pageSize"
                    class="form-control form-control-sm"
                    @change="page = 1"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                  </select></label
                ><button
                  class="btn btn-light btn-sm"
                  :disabled="page === 1"
                  @click="page--"
                >
                  Previous</button
                ><span>{{ page }} / {{ pageCount }}</span
                ><button
                  class="btn btn-light btn-sm"
                  :disabled="page >= pageCount"
                  @click="page++"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>
        <p class="report-footnote">
          Overall percentage is calculated from summed marks divided by summed
          obtainable marks; zero or missing maximums are excluded. Only students
          with results for every selected subject receive a rank. Live pass and
          grade classifications use the selected school grading scale when it
          defines grade boundaries.
        </p>
      </div>
      <div
        v-else-if="!loading && !errorMessage"
        class="welcome-state card border-0 shadow-sm"
      >
        <span><i class="fa fa-chart-column"></i></span>
        <h2>Build an academic ranking</h2>
        <p>
          Select a registered term, class section, and assessment type, then
          apply filters to compare student results.
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import PageSpinner from "@/components/PageSpinner";

const SAMPLE_CLASSES = [
  {
    id: "c1",
    name: "Primary 4",
    options: [
      { id: "o1", name: "General", arms: ["Blue", "Gold"] },
      { id: "o2", name: "French", arms: ["A"] },
    ],
  },
  {
    id: "c2",
    name: "Primary 5",
    options: [{ id: "o3", name: "General", arms: ["Blue", "Gold"] }],
  },
  {
    id: "c3",
    name: "JSS 1",
    options: [{ id: "o4", name: "Science", arms: ["A", "B"] }],
  },
];
const SAMPLE_SUBJECTS = [
  "English Language",
  "Mathematics",
  "Basic Science",
  "Social Studies",
  "Computer Studies",
  "Creative Arts",
  "French",
  "Civic Education",
];
const SAMPLE_TERMS = [
  {
    registeredTermId: "demo-2024-3",
    schSession: "2024/2025",
    schTerm: "Third Term",
  },
  {
    registeredTermId: "demo-2025-1",
    schSession: "2025/2026",
    schTerm: "First Term",
  },
  {
    registeredTermId: "demo-2025-2",
    schSession: "2025/2026",
    schTerm: "Second Term",
  },
];
const SAMPLE_GRADES = [
  { Grade: "A", Title: "Excellent", Markfrom: 80, Markto: 100 },
  { Grade: "B", Title: "Very Good", Markfrom: 70, Markto: 79.99 },
  { Grade: "C", Title: "Good", Markfrom: 60, Markto: 69.99 },
  { Grade: "D", Title: "Pass", Markfrom: 50, Markto: 59.99 },
  { Grade: "F", Title: "Fail", Markfrom: 0, Markto: 49.99 },
];

export default {
  name: "ClassPerformingRanking",
  components: { PageSpinner },
  data() {
    return {
      loading: false,
      errorMessage: "",
      hasGenerated: false,
      mockMode: false,
      listLoading: {
        terms: false,
        classes: false,
        options: false,
        sections: false,
        students: false,
        subjects: false,
        gradeScales: false,
      },
      terms: [],
      classes: [],
      options: [],
      sections: [],
      students: [],
      subjects: [],
      gradeCategories: [],
      gradeScale: [],
      rows: [],
      search: "",
      sortKey: "rank",
      sortDirection: "asc",
      page: 1,
      pageSize: 20,
      requestId: 0,
      cascadeId: 0,
      gradeScaleRequestId: 0,
      filters: {
        RegisteredTermId: "",
        ClassId: "",
        OptionId: "",
        SectionId: "",
        StudentId: "",
        AssessmentType: "full",
        SubjectId: "",
        GradeCategoryId: "",
      },
      appliedFilters: null,
      mockTerms: SAMPLE_TERMS,
      mockClasses: SAMPLE_CLASSES,
      mockSubjects: SAMPLE_SUBJECTS.map((name, index) => ({
        Id: `sub-${index + 1}`,
        SchSubject: name,
      })),
      mockGrades: SAMPLE_GRADES,
      mockRecords: this.createMockRecords(),
    };
  },
  computed: {
    scopeLabel() {
      const section = this.sections.find(
        (item) =>
          String(item.Id) ===
          String(this.appliedFilters && this.appliedFilters.SectionId)
      );
      if (section) return `${section.SectionName} section`;
      if (this.appliedFilters && this.appliedFilters.OptionId)
        return `${this.optionNameFor(this.appliedFilters.OptionId)} option`;
      if (this.appliedFilters && this.appliedFilters.ClassId)
        return `${this.classNameFor(this.appliedFilters.ClassId)} class`;
      return this.mockMode ? "selected filters" : "selected section";
    },
    rankedRows() {
      const roster = this.rows.map((row) => {
        const grade = row.complete ? this.gradeFor(row.percentage) : null;
        const passed =
          this.passMark === null
            ? 0
            : row.subjectResults.filter(
                (item) =>
                  item.percentage !== null && item.percentage >= this.passMark
              ).length;
        const failed =
          this.passMark === null
            ? 0
            : row.subjectResults.filter(
                (item) =>
                  item.percentage !== null && item.percentage < this.passMark
              ).length;
        const status = !row.complete
          ? "Incomplete"
          : this.passMark !== null && row.percentage < this.passMark
          ? "Needs support"
          : "On track";
        return Object.assign({}, row, {
          grade: grade ? grade.Grade : "",
          passed,
          failed,
          status,
          subjectResults: row.subjectResults.map((item) => {
            const subjectGrade =
              item.percentage === null ? null : this.gradeFor(item.percentage);
            return Object.assign({}, item, {
              grade: subjectGrade ? subjectGrade.Grade : "",
            });
          }),
        });
      });
      const rankable = roster
        .filter((row) => row.complete)
        .slice()
        .sort(
          (a, b) =>
            b.percentage - a.percentage ||
            b.totalObtained - a.totalObtained ||
            a.name.localeCompare(b.name)
        );
      let lastPercentage = null;
      let rank = 0;
      rankable.forEach((row, index) => {
        if (row.percentage !== lastPercentage) rank = index + 1;
        row.rank = rank;
        lastPercentage = row.percentage;
      });
      return roster;
    },
    filteredRows() {
      const needle = this.search.toLowerCase();
      return this.rankedRows
        .filter(
          (row) =>
            (!this.appliedFilters ||
              !this.appliedFilters.StudentId ||
              row.id === this.appliedFilters.StudentId) &&
            (!needle ||
              `${row.name} ${row.admission}`.toLowerCase().includes(needle))
        )
        .slice()
        .sort((a, b) => {
          const aVal = a[this.sortKey];
          const bVal = b[this.sortKey];
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          const compare =
            typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
          return this.sortDirection === "asc" ? compare : -compare;
        });
    },
    completeStudents() {
      return this.rankedRows.filter((row) => row.complete);
    },
    averageScore() {
      return this.average(this.completeStudents.map((row) => row.percentage));
    },
    medianScore() {
      const values = this.completeStudents
        .map((row) => row.percentage)
        .sort((a, b) => a - b);
      if (!values.length) return null;
      const mid = Math.floor(values.length / 2);
      return values.length % 2
        ? values[mid]
        : (values[mid - 1] + values[mid]) / 2;
    },
    passMark() {
      const failing = this.gradeScale.filter(
        (item) =>
          String(item.Grade || "").toUpperCase() === "F" ||
          /fail/i.test(item.Title || "")
      );
      const passing = this.gradeScale.filter(
        (item) =>
          String(item.Grade || "").toUpperCase() !== "F" &&
          !/fail/i.test(item.Title || "")
      );
      if (!passing.length || !failing.length) return null;
      const boundary = Math.min(
        ...passing.map((item) => Number(item.Markfrom)).filter(Number.isFinite)
      );
      return Number.isFinite(boundary) ? boundary : null;
    },
    passedStudents() {
      return this.passMark === null
        ? []
        : this.completeStudents.filter(
            (row) => row.percentage >= this.passMark
          );
    },
    failedStudents() {
      return this.passMark === null
        ? []
        : this.completeStudents.filter((row) => row.percentage < this.passMark);
    },
    supportStudents() {
      return this.failedStudents;
    },
    passRate() {
      return this.completeStudents.length
        ? Math.round(
            (this.passedStudents.length * 100) / this.completeStudents.length
          )
        : 0;
    },
    subjectSummary() {
      const grouped = {};
      this.rankedRows.forEach((row) =>
        row.subjectResults.forEach((result) => {
          if (result.percentage === null) return;
          if (!grouped[result.id])
            grouped[result.id] = {
              id: result.id,
              name: result.name,
              total: 0,
              students: 0,
              highest: 0,
              lowest: 100,
            };
          const item = grouped[result.id];
          item.total += result.percentage;
          item.students += 1;
          item.highest = Math.max(item.highest, result.percentage);
          item.lowest = Math.min(item.lowest, result.percentage);
        })
      );
      return Object.keys(grouped)
        .map((key) =>
          Object.assign({}, grouped[key], {
            average: Math.round(grouped[key].total / grouped[key].students),
          })
        )
        .sort((a, b) => b.average - a.average);
    },
    comparisonTitle() {
      if (this.filters.OptionId) return "Performance by section";
      if (this.filters.ClassId) return "Performance by option";
      return "Performance by class";
    },
    groupComparison() {
      if (this.filters.SectionId || this.filters.StudentId) return [];
      const groupKey = this.filters.OptionId
        ? "sectionName"
        : this.filters.ClassId
        ? "optionName"
        : "className";
      const groups = {};
      this.rankedRows
        .filter((row) => row.complete)
        .forEach((row) => {
          const label = row[groupKey] || "Unspecified";
          const key = `${row.className}|${row.optionName}|${row.sectionName}|${label}`;
          if (!groups[key]) groups[key] = { key, label, total: 0, count: 0 };
          groups[key].total += row.percentage;
          groups[key].count += 1;
        });
      return Object.keys(groups)
        .map((key) => ({
          key,
          label: groups[key].label,
          average: Math.round(groups[key].total / groups[key].count),
        }))
        .sort((a, b) => b.average - a.average)
        .slice(0, 8);
    },
    highestSubject() {
      return this.subjectSummary[0] || null;
    },
    lowestSubject() {
      return this.subjectSummary.length
        ? this.subjectSummary[this.subjectSummary.length - 1]
        : null;
    },
    gradeBands() {
      if (!this.gradeScale.length) return [];
      return this.gradeScale
        .slice()
        .sort((a, b) => Number(b.Markfrom) - Number(a.Markfrom))
        .map((grade, index) => {
          const min = Number(grade.Markfrom);
          const max = Number(grade.Markto);
          const count = this.completeStudents.filter(
            (row) => row.percentage >= min && row.percentage <= max
          ).length;
          return {
            label: `${grade.Grade} · ${grade.Title || `${min}–${max}%`}`,
            count,
            color: [
              "#31845f",
              "#5a91d6",
              "#8e79c6",
              "#d09a35",
              "#d96c66",
              "#71839a",
            ][index % 6],
          };
        });
    },
    maxBandCount() {
      return Math.max(1, ...this.gradeBands.map((item) => item.count));
    },
    gradeDistribution() {
      const total = this.completeStudents.length;
      return this.gradeBands
        .map((item) =>
          Object.assign({}, item, {
            percent: total ? ((item.count * 100) / total).toFixed(1) : "0.0",
          })
        )
        .filter((item) => item.count > 0);
    },
    gradeDonutStyle() {
      let current = 0;
      const stops = this.gradeDistribution.map((item) => {
        const start = current;
        current += Number(item.percent);
        return `${item.color} ${start}% ${current}%`;
      });
      return {
        background: stops.length
          ? `conic-gradient(${stops.join(",")})`
          : "#e9edf3",
      };
    },
    topStudents() {
      return this.completeStudents
        .slice()
        .sort((a, b) => a.rank - b.rank)
        .slice(0, 7);
    },
    trendData() {
      if (!this.mockMode) return [];
      const selectedTermIndex = SAMPLE_TERMS.findIndex(
        (term) =>
          term.registeredTermId ===
          (this.appliedFilters && this.appliedFilters.RegisteredTermId)
      );
      return SAMPLE_TERMS.slice(0, selectedTermIndex + 1).map(
        (term, index) => ({
          label: `${term.schTerm.slice(0, 2)} ${term.schSession.slice(2, 4)}`,
          value: this.mockAverageForTerm(term.registeredTermId),
          x: 45 + index * 210,
        })
      );
    },
    trendLine() {
      return this.trendData
        .map((item) => `${item.x},${this.trendY(item.value)}`)
        .join(" ");
    },
    passRingStyle() {
      return {
        background: `conic-gradient(#338866 ${this.passRate}%, #e46b6b 0)`,
      };
    },
    metrics() {
      const average = this.averageScore;
      const totalObtained = this.completeStudents.reduce(
        (sum, row) => sum + row.totalObtained,
        0
      );
      const totalMaximum = this.completeStudents.reduce(
        (sum, row) => sum + row.totalMaximum,
        0
      );
      const topGrade = this.gradeScale
        .slice()
        .sort((a, b) => Number(b.Markfrom) - Number(a.Markfrom))[0];
      const excellent = topGrade
        ? this.completeStudents.filter(
            (row) => row.percentage >= Number(topGrade.Markfrom)
          ).length
        : null;
      return [
        {
          label: "Students ranked",
          value: this.completeStudents.length,
          note: `${
            this.rankedRows.length - this.completeStudents.length
          } incomplete`,
          icon: "fa-ranking-star",
          tone: "blue",
          help: "Only students with results in every selected subject are ranked.",
        },
        {
          label: "Class average",
          value: average === null ? "—" : `${average.toFixed(1)}%`,
          note: "Weighted by obtainable marks",
          icon: "fa-chart-line",
          tone: "green",
          help: "Sum of marks divided by sum of obtainable marks.",
        },
        {
          label: "Highest score",
          value: this.completeStudents.length
            ? `${Math.max(
                ...this.completeStudents.map((row) => row.percentage)
              ).toFixed(1)}%`
            : "—",
          note: "Complete results only",
          icon: "fa-arrow-trend-up",
          tone: "violet",
        },
        {
          label: "Lowest score",
          value: this.completeStudents.length
            ? `${Math.min(
                ...this.completeStudents.map((row) => row.percentage)
              ).toFixed(1)}%`
            : "—",
          note: "Complete results only",
          icon: "fa-arrow-trend-down",
          tone: "amber",
        },
        {
          label: "Median score",
          value:
            this.medianScore === null ? "—" : `${this.medianScore.toFixed(1)}%`,
          note: "Complete results only",
          icon: "fa-chart-simple",
          tone: "slate",
        },
        {
          label: "Average pass rate",
          value: this.passMark === null ? "—" : `${this.passRate}%`,
          note:
            this.passMark === null
              ? "Select grading scale"
              : `Pass mark ${this.passMark}%`,
          icon: "fa-circle-check",
          tone: "green",
        },
        {
          label: "Students passed",
          value: this.passMark === null ? "—" : this.passedStudents.length,
          note: "Complete results only",
          icon: "fa-user-check",
          tone: "teal",
        },
        {
          label: "Students failed",
          value: this.passMark === null ? "—" : this.failedStudents.length,
          note: "Complete results only",
          icon: "fa-user-xmark",
          tone: "red",
        },
        {
          label: "Excellent performers",
          value: excellent === null ? "—" : excellent,
          note: topGrade
            ? `Top category: ${topGrade.Grade}`
            : "Select grading scale",
          icon: "fa-award",
          tone: "violet",
        },
        {
          label: "Need academic support",
          value: this.passMark === null ? "—" : this.supportStudents.length,
          note: "Below configured pass mark",
          icon: "fa-life-ring",
          tone: "amber",
        },
        {
          label: "Strongest subject",
          value: this.highestSubject ? this.highestSubject.name : "—",
          note: this.highestSubject
            ? `${this.highestSubject.average}% class average`
            : "No subject data",
          icon: "fa-star",
          tone: "blue",
        },
        {
          label: "Needs attention",
          value: this.lowestSubject ? this.lowestSubject.name : "—",
          note: this.lowestSubject
            ? `${this.lowestSubject.average}% class average`
            : "No subject data",
          icon: "fa-bullseye",
          tone: "red",
        },
        {
          label: "Marks obtained",
          value: totalMaximum
            ? `${this.formatNumber(totalObtained)} / ${this.formatNumber(
                totalMaximum
              )}`
            : "—",
          note: "Complete ranked students",
          icon: "fa-square-poll-vertical",
          tone: "slate",
        },
      ];
    },
    columns() {
      return [
        { key: "rank", label: "Rank" },
        { key: "admission", label: "Admission no." },
        { key: "name", label: "Student" },
        { key: "className", label: "Class" },
        { key: "optionName", label: "Option" },
        { key: "sectionName", label: "Section" },
        { key: "subjectCount", label: "Subjects offered" },
        { key: "resultsCount", label: "With results" },
        { key: "totalObtained", label: "Marks obtained" },
        { key: "totalMaximum", label: "Maximum marks" },
        { key: "percentage", label: "Average" },
        { key: "grade", label: "Grade" },
        { key: "passed", label: "Passed" },
        { key: "failed", label: "Failed" },
        { key: "status", label: "Status" },
      ];
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pageStart() {
      return (this.page - 1) * this.pageSize;
    },
    pagedRows() {
      return this.filteredRows.slice(
        this.pageStart,
        this.pageStart + this.pageSize
      );
    },
    selectedStudent() {
      return (
        this.rankedRows.find((row) => row.id === this.filters.StudentId) || null
      );
    },
    activeFilters() {
      if (!this.appliedFilters) return [];
      const f = this.appliedFilters;
      const labels = [];
      const term = this.terms.find(
        (item) => String(item.registeredTermId) === f.RegisteredTermId
      );
      if (term) labels.push(`${term.schSession} · ${term.schTerm}`);
      const cls = this.classes.find((item) => String(item.Id) === f.ClassId);
      if (cls) labels.push(cls.Sch_Class);
      const option = this.options.find(
        (item) => String(item.Id) === f.OptionId
      );
      if (option) labels.push(option.ClassOption);
      const section = this.sections.find(
        (item) => String(item.Id) === f.SectionId
      );
      if (section) labels.push(section.SectionName);
      const student = this.students.find(
        (item) => String(this.studentId(item)) === f.StudentId
      );
      if (student) labels.push(this.studentName(student));
      const subject = this.subjects.find(
        (item) => String(item.Id) === f.SubjectId
      );
      if (subject) labels.push(subject.SchSubject);
      labels.push(f.AssessmentType === "mid" ? "Mid-term" : "Full term");
      const grade = this.gradeCategories.find(
        (item) => String(item.Id) === f.GradeCategoryId
      );
      if (grade) labels.push(grade.CategoryName);
      return labels;
    },
  },
  watch: {
    pageCount(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    createMockRecords() {
      const records = [];
      let studentNumber = 0;
      const firstNames = [
        "Amina",
        "Daniel",
        "Zara",
        "Tunde",
        "Chika",
        "Samuel",
        "Maryam",
        "David",
        "Ada",
        "Ibrahim",
        "Grace",
        "Musa",
      ];
      const lastNames = [
        "Okafor",
        "Bello",
        "Ibrahim",
        "Adeyemi",
        "Nwosu",
        "Ojo",
        "Yusuf",
        "Eze",
        "Abubakar",
        "Aliyu",
        "Okeke",
        "Garba",
      ];
      SAMPLE_CLASSES.forEach((cls) =>
        cls.options.forEach((option) =>
          option.arms.forEach((arm) => {
            for (let index = 0; index < 12; index += 1) {
              studentNumber += 1;
              const id = `demo-student-${studentNumber}`;
              const baseline = [94, 84, 76, 67, 58, 48, 88, 72, 61, 53, 91, 79][
                index
              ];
              const subjectResults = SAMPLE_SUBJECTS.map(
                (name, subjectIndex) => {
                  if (studentNumber % 11 === 0 && subjectIndex === 7)
                    return {
                      id: `sub-${subjectIndex + 1}`,
                      name,
                      ca: 0,
                      exam: 0,
                      obtained: 0,
                      maximum: 0,
                      percentage: null,
                    };
                  const maximum = subjectIndex % 2 === 0 ? 100 : 80;
                  let percentage = Math.max(
                    22,
                    Math.min(
                      100,
                      baseline +
                        ((studentNumber * 7 + subjectIndex * 11) % 21) -
                        10
                    )
                  );
                  if ([1, 13].includes(studentNumber)) percentage = 82;
                  const caMaximum = maximum * 0.4;
                  const obtained = Math.round((maximum * percentage) / 100);
                  const ca = Math.round((obtained * caMaximum) / maximum);
                  const exam = obtained - ca;
                  return {
                    id: `sub-${subjectIndex + 1}`,
                    name,
                    ca,
                    exam,
                    obtained,
                    maximum,
                    percentage: Math.round((obtained * 100) / maximum),
                  };
                }
              );
              const start = new Date("2025-09-08T12:00:00");
              const sessionVariation = SAMPLE_TERMS.map((term, termIndex) => ({
                termId: term.registeredTermId,
                subjectResults: subjectResults.map((item) =>
                  Object.assign({}, item, {
                    percentage:
                      item.percentage === null
                        ? null
                        : Math.max(
                            0,
                            Math.min(
                              100,
                              item.percentage +
                                (termIndex === 1 ? 2 : termIndex === 2 ? -3 : 0)
                            )
                          ),
                  })
                ),
              }));
              const sectionId = `${option.id}-${arm}`;
              SAMPLE_TERMS.forEach((term, termIndex) => {
                const termResults = sessionVariation[termIndex].subjectResults;
                const student = {
                  id,
                  termId: term.registeredTermId,
                  classId: cls.id,
                  optionId: option.id,
                  sectionId,
                  studentId: id,
                  name: `${
                    firstNames[(studentNumber - 1) % firstNames.length]
                  } ${lastNames[(studentNumber * 3) % lastNames.length]}`,
                  admission: `SD-${String(202600 + studentNumber).padStart(
                    6,
                    "0"
                  )}`,
                  className: cls.name,
                  optionName: option.name,
                  sectionName: arm,
                  previousAverage: Math.max(0, baseline - 4),
                  startDate: start.toISOString().slice(0, 10),
                };
                records.push(
                  Object.assign({}, student, {
                    assessmentType: "full",
                    subjectResults: termResults,
                  })
                );
                records.push(
                  Object.assign({}, student, {
                    assessmentType: "mid",
                    subjectResults: termResults.map((item) =>
                      Object.assign({}, item, {
                        ca: item.ca / 2,
                        exam: item.exam / 2,
                        maximum: item.maximum / 2,
                        obtained: item.obtained / 2,
                      })
                    ),
                  })
                );
              });
            }
          })
        )
      );
      return records;
    },
    getArray(value) {
      if (Array.isArray(value)) return value;
      if (value && Array.isArray(value.data)) return value.data;
      return [];
    },
    studentId(item) {
      return item.studentId || item.StudentId || item.Id || item.id;
    },
    studentName(item) {
      return (
        item.studentName ||
        item.StudentName ||
        item.fullName ||
        item.FullName ||
        "Student"
      );
    },
    normalizeStudent(item) {
      return {
        id: String(this.studentId(item)),
        admission:
          item.admNo ||
          item.AdmNo ||
          item.admissionNumber ||
          item.AdmissionNumber ||
          "—",
        name: this.studentName(item),
        className:
          item.className ||
          item.sch_Class ||
          item.Sch_Class ||
          this.classNameFor(this.filters.ClassId),
        optionName:
          item.classOption ||
          item.ClassOption ||
          this.optionNameFor(this.filters.OptionId),
        sectionName:
          item.sectionName ||
          item.SectionName ||
          this.sectionNameFor(this.filters.SectionId),
      };
    },
    classNameFor(id) {
      const item = this.classes.find((row) => String(row.Id) === String(id));
      return item ? item.Sch_Class || item.name : "—";
    },
    optionNameFor(id) {
      const item = this.options.find((row) => String(row.Id) === String(id));
      return item ? item.ClassOption || item.name : "—";
    },
    sectionNameFor(id) {
      const item = this.sections.find((row) => String(row.Id) === String(id));
      return item ? item.SectionName || item.name : "—";
    },
    average(values) {
      return values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    },
    mockAverageForTerm(termId) {
      let data = this.mockRecords.filter(
        (row) =>
          row.termId === termId &&
          row.assessmentType ===
            (this.appliedFilters && this.appliedFilters.AssessmentType)
      );
      const f = this.appliedFilters || {};
      if (f.ClassId) data = data.filter((row) => row.classId === f.ClassId);
      if (f.OptionId) data = data.filter((row) => row.optionId === f.OptionId);
      if (f.SectionId)
        data = data.filter((row) => row.sectionId === f.SectionId);
      if (f.StudentId) data = data.filter((row) => row.id === f.StudentId);
      const selectedSubjects = f.SubjectId
        ? this.mockSubjects.filter(
            (subject) => String(subject.Id) === f.SubjectId
          )
        : this.mockSubjects;
      const values = data
        .map((row) => this.materializeMockRow(row, selectedSubjects))
        .filter((row) => row.complete)
        .map((row) => row.percentage);
      return Math.round((this.average(values) || 0) * 10) / 10;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString(undefined, {
        maximumFractionDigits: 1,
      });
    },
    trendY(value) {
      return 160 - Number(value) * 1.35;
    },
    barWidth(value, max) {
      return max ? Math.max(4, (value * 100) / max) : 0;
    },
    gradeFor(percentage) {
      return (
        this.gradeScale.find(
          (item) =>
            percentage >= Number(item.Markfrom) &&
            percentage <= Number(item.Markto)
        ) || null
      );
    },
    materializeRows(sourceRecords, roster, subjects, assessmentType) {
      const byId = {};
      roster.forEach((item) => {
        const student = this.normalizeStudent(item);
        byId[student.id] = Object.assign(student, {
          subjectResults: [],
          subjectCount: subjects.length,
          complete: false,
        });
      });
      sourceRecords.forEach(({ subject, marks }) =>
        marks.forEach((mark) => {
          const id = String(mark.studentId || mark.StudentId || mark.id);
          if (!byId[id])
            byId[id] = Object.assign(this.normalizeStudent(mark), {
              subjectResults: [],
              subjectCount: subjects.length,
            });
          const ca = Number(mark.ca || mark.CA || 0);
          const exam = Number(mark.exam || mark.ExamScore || 0);
          const caMax = Number(
            mark.obtainableCAScore || mark.ObtainableCAScore || 0
          );
          const examMax = Number(
            mark.obtainableExamScore || mark.ObtainableExamScore || 0
          );
          const maximum = caMax + examMax;
          const obtained = ca + exam;
          byId[id].subjectResults.push({
            id: String(subject.Id),
            name: subject.SchSubject,
            ca,
            exam,
            obtained,
            maximum,
            percentage:
              maximum > 0 ? Math.round((obtained * 1000) / maximum) / 10 : null,
          });
        })
      );
      return Object.keys(byId).map((id) => {
        const row = byId[id];
        if (this.filters.SubjectId)
          row.subjectResults = row.subjectResults.filter(
            (item) => item.id === this.filters.SubjectId
          );
        row.subjectCount = this.filters.SubjectId ? 1 : subjects.length;
        row.resultsCount = row.subjectResults.filter(
          (item) => item.maximum > 0
        ).length;
        row.totalObtained = row.subjectResults.reduce(
          (sum, item) => sum + item.obtained,
          0
        );
        row.totalMaximum = row.subjectResults.reduce(
          (sum, item) => sum + item.maximum,
          0
        );
        row.percentage = row.totalMaximum
          ? Math.round((row.totalObtained * 1000) / row.totalMaximum) / 10
          : 0;
        row.complete =
          row.resultsCount === row.subjectCount && row.subjectCount > 0;
        row.grade = this.gradeFor(row.percentage);
        row.grade = row.grade ? row.grade.Grade : "";
        row.passed =
          this.passMark === null
            ? 0
            : row.subjectResults.filter(
                (item) =>
                  item.percentage !== null && item.percentage >= this.passMark
              ).length;
        row.failed =
          this.passMark === null
            ? 0
            : row.subjectResults.filter(
                (item) =>
                  item.percentage !== null && item.percentage < this.passMark
              ).length;
        row.status = !row.complete
          ? "Incomplete"
          : this.passMark !== null && row.percentage < this.passMark
          ? "Needs support"
          : "On track";
        row.assessmentType = assessmentType;
        return row;
      });
    },
    isAtRisk(row) {
      return (
        this.passMark !== null && row.complete && row.percentage < this.passMark
      );
    },
    statusLabel(row) {
      return row.status || (!row.complete ? "Incomplete" : "Complete");
    },
    statusClass(row) {
      return !row.complete
        ? "status-incomplete"
        : this.isAtRisk(row)
        ? "status-risk"
        : "status-good";
    },
    sortBy(key) {
      if (this.sortKey === key)
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      else {
        this.sortKey = key;
        this.sortDirection = key === "rank" ? "asc" : "desc";
      }
      this.page = 1;
    },
    sortIcon(key) {
      return this.sortKey !== key
        ? "fa-sort text-muted"
        : this.sortDirection === "asc"
        ? "fa-sort-up"
        : "fa-sort-down";
    },
    async loadInitialLists() {
      this.listLoading.terms = true;
      this.listLoading.classes = true;
      this.listLoading.gradeScales = true;
      try {
        const [termResponse, classResponse, categoryResponse] =
          await Promise.all([
            this.$store.dispatch(
              "PortalSchoolSessionTerm/GetPortalRegisteredTerm",
              { SearchValue: null, PageNumber: 1, PageSize: 100 }
            ),
            this.$store.dispatch("getclassinsubjectassigned"),
            this.$store.dispatch("getclasscategory").catch(() => {
              this.errorMessage =
                "Grading categories could not be loaded. Scores can still be reviewed, but grade and pass analysis will be unavailable.";
              return [];
            }),
          ]);
        this.terms = this.getArray(termResponse);
        this.classes = this.getArray(classResponse);
        this.gradeCategories = this.getArray(categoryResponse);
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load academic filters."
        );
      } finally {
        this.listLoading.terms = false;
        this.listLoading.classes = false;
        this.listLoading.gradeScales = false;
      }
    },
    async onClassChange() {
      const request = ++this.cascadeId;
      this.listLoading.options = false;
      this.listLoading.sections = false;
      this.listLoading.students = false;
      this.listLoading.subjects = false;
      this.filters.OptionId = "";
      this.filters.SectionId = "";
      this.filters.StudentId = "";
      this.filters.SubjectId = "";
      this.options = [];
      this.sections = [];
      this.students = [];
      this.subjects = [];
      this.rows = [];
      if (!this.filters.ClassId) return;
      if (this.mockMode) {
        const item = this.mockClasses.find(
          (row) => row.id === this.filters.ClassId
        );
        this.options = item
          ? item.options.map((option) => ({
              Id: option.id,
              ClassOption: option.name,
            }))
          : [];
        return;
      }
      this.listLoading.options = true;
      try {
        const response = await this.$store.dispatch(
          "getoptioninsubjectassigned",
          this.filters.ClassId
        );
        if (request === this.cascadeId) this.options = this.getArray(response);
      } catch (error) {
        if (request === this.cascadeId)
          this.errorMessage = this.errorText(
            error,
            "Could not load class options."
          );
      } finally {
        if (request === this.cascadeId) this.listLoading.options = false;
      }
    },
    async onOptionChange() {
      const request = ++this.cascadeId;
      this.listLoading.sections = false;
      this.listLoading.students = false;
      this.listLoading.subjects = false;
      this.filters.SectionId = "";
      this.filters.StudentId = "";
      this.filters.SubjectId = "";
      this.sections = [];
      this.students = [];
      this.subjects = [];
      this.rows = [];
      if (!this.filters.OptionId) return;
      if (this.mockMode) {
        const option = this.mockClasses
          .reduce((all, cls) => all.concat(cls.options), [])
          .find((row) => row.id === this.filters.OptionId);
        this.sections = option
          ? option.arms.map((arm) => ({
              Id: `${option.id}-${arm}`,
              SectionName: arm,
            }))
          : [];
        return;
      }
      this.listLoading.sections = true;
      try {
        const response = await this.$store.dispatch(
          "getarminsubjectassigned",
          this.filters.OptionId
        );
        if (request === this.cascadeId) this.sections = this.getArray(response);
      } catch (error) {
        if (request === this.cascadeId)
          this.errorMessage = this.errorText(
            error,
            "Could not load class sections."
          );
      } finally {
        if (request === this.cascadeId) this.listLoading.sections = false;
      }
    },
    async onSectionChange() {
      const request = ++this.cascadeId;
      this.listLoading.students = false;
      this.listLoading.subjects = false;
      this.filters.StudentId = "";
      this.filters.SubjectId = "";
      this.students = [];
      this.subjects = [];
      this.rows = [];
      if (!this.filters.SectionId) return;
      if (this.mockMode) {
        this.students = this.sampleRowsForTerm()
          .filter(
            (row) =>
              row.sectionId === this.filters.SectionId &&
              row.assessmentType === this.filters.AssessmentType
          )
          .map((row) => ({
            studentId: row.id,
            studentName: row.name,
            admNo: row.admission,
          }));
        this.subjects = this.mockSubjects;
        return;
      }
      this.listLoading.sections = true;
      this.listLoading.students = true;
      this.listLoading.subjects = true;
      try {
        const [arms, roster] = await Promise.all([
          this.$store.dispatch(
            "getschsubjectinsubjectassigned",
            this.filters.SectionId
          ),
          this.$store.dispatch("PortalStudent/GetStudentForReportCard", {
            RegisteredTermId: this.filters.RegisteredTermId,
            ClassId: this.filters.ClassId || 0,
            SectionId: this.filters.SectionId,
            OptionId: this.filters.OptionId || 0,
          }),
        ]);
        if (request !== this.cascadeId) return;
        this.subjects = this.getArray(arms);
        this.students = this.getArray(
          roster && roster.responseCode === "00" ? roster.data : roster
        );
      } catch (error) {
        if (request === this.cascadeId)
          this.errorMessage = this.errorText(
            error,
            "Could not load students and assigned subjects for this section."
          );
      } finally {
        if (request === this.cascadeId) {
          this.listLoading.sections = false;
          this.listLoading.students = false;
          this.listLoading.subjects = false;
        }
      }
    },
    async loadGradeScale() {
      this.gradeScale = [];
      if (!this.filters.GradeCategoryId) {
        this.gradeScaleRequestId += 1;
        this.listLoading.gradeScales = false;
        return;
      }
      if (this.mockMode) {
        this.gradeScale = this.mockGrades;
        return;
      }
      const request = ++this.gradeScaleRequestId;
      this.listLoading.gradeScales = true;
      try {
        const response = await this.$store.dispatch(
          "getgradescale",
          this.filters.GradeCategoryId
        );
        if (request === this.gradeScaleRequestId)
          this.gradeScale = this.getArray(response);
      } catch (error) {
        if (request === this.gradeScaleRequestId)
          this.errorMessage = this.errorText(
            error,
            "Could not load the selected grading scale."
          );
      } finally {
        if (request === this.gradeScaleRequestId)
          this.listLoading.gradeScales = false;
      }
    },
    async applyFilters() {
      this.errorMessage = "";
      this.search = "";
      this.page = 1;
      if (!this.filters.RegisteredTermId) {
        this.errorMessage = "Select a registered term.";
        return;
      }
      if (!this.mockMode && !this.filters.SectionId) {
        this.errorMessage =
          "Select a section (Arm). Live mark records are retrieved by section and subject.";
        return;
      }
      this.appliedFilters = Object.assign({}, this.filters);
      if (this.mockMode) {
        let data = this.sampleRowsForTerm().filter(
          (row) => row.assessmentType === this.filters.AssessmentType
        );
        if (this.filters.ClassId)
          data = data.filter((row) => row.classId === this.filters.ClassId);
        if (this.filters.OptionId)
          data = data.filter((row) => row.optionId === this.filters.OptionId);
        if (this.filters.SectionId)
          data = data.filter((row) => row.sectionId === this.filters.SectionId);
        const subjects = this.filters.SubjectId
          ? this.mockSubjects.filter(
              (item) => String(item.Id) === this.filters.SubjectId
            )
          : this.mockSubjects;
        this.rows = data.map((row) => this.materializeMockRow(row, subjects));
        this.hasGenerated = true;
        return;
      }
      const request = ++this.requestId;
      this.loading = true;
      this.hasGenerated = false;
      try {
        const subjects = this.filters.SubjectId
          ? this.subjects.filter(
              (item) => String(item.Id) === this.filters.SubjectId
            )
          : this.subjects;
        if (!subjects.length)
          throw new Error(
            "No assigned subjects are available for this section."
          );
        const action =
          this.filters.AssessmentType === "mid"
            ? "PortalMarkRecord/GetStudentForMarkRecordMidTerm"
            : "PortalMarkRecord/GetStudentForMarkRecord";
        const sourceRecords = await Promise.all(
          subjects.map(async (subject) => {
            const response = await this.$store.dispatch(action, {
              SectionId: this.filters.SectionId,
              RegisteredTermId: this.filters.RegisteredTermId,
              SubjectId: subject.Id,
            });
            if (
              response &&
              response.responseCode &&
              response.responseCode !== "00"
            )
              throw new Error(
                response.responseMessage ||
                  `Could not load ${subject.SchSubject} marks.`
              );
            return {
              subject,
              marks: this.getArray(
                response && response.responseCode === "00"
                  ? response.data
                  : response
              ),
            };
          })
        );
        if (request !== this.requestId) return;
        let roster = this.students;
        if (!roster.length) {
          const response = await this.$store.dispatch(
            "PortalStudent/GetStudentForReportCard",
            {
              RegisteredTermId: this.filters.RegisteredTermId,
              ClassId: this.filters.ClassId || 0,
              SectionId: this.filters.SectionId,
              OptionId: this.filters.OptionId || 0,
            }
          );
          roster = this.getArray(
            response && response.responseCode === "00"
              ? response.data
              : response
          );
        }
        this.rows = this.materializeRows(
          sourceRecords,
          roster,
          subjects,
          this.filters.AssessmentType
        );
        this.hasGenerated = true;
      } catch (error) {
        if (request === this.requestId)
          this.errorMessage = this.errorText(
            error,
            "Could not load student marks."
          );
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    materializeMockRow(source, subjects) {
      const results = source.subjectResults.filter((item) =>
        subjects.some((subject) => String(subject.Id) === item.id)
      );
      const totalObtained = results.reduce(
        (sum, item) => sum + item.obtained,
        0
      );
      const totalMaximum = results.reduce((sum, item) => sum + item.maximum, 0);
      const percentage = totalMaximum
        ? Math.round((totalObtained * 1000) / totalMaximum) / 10
        : 0;
      const complete =
        results.length === subjects.length &&
        results.every((item) => item.maximum > 0);
      return {
        id: source.id,
        studentId: source.id,
        admission: source.admission,
        name: source.name,
        className: source.className,
        optionName: source.optionName,
        sectionName: source.sectionName,
        subjectResults: results.map((item) =>
          Object.assign({}, item, {
            grade:
              this.gradeFor(item.percentage) &&
              this.gradeFor(item.percentage).Grade,
          })
        ),
        subjectCount: subjects.length,
        resultsCount: results.filter((item) => item.maximum > 0).length,
        totalObtained,
        totalMaximum,
        percentage,
        complete,
        grade:
          (this.gradeFor(percentage) && this.gradeFor(percentage).Grade) || "",
        passed:
          this.passMark === null
            ? 0
            : results.filter(
                (item) =>
                  item.percentage !== null && item.percentage >= this.passMark
              ).length,
        failed:
          this.passMark === null
            ? 0
            : results.filter(
                (item) =>
                  item.percentage !== null && item.percentage < this.passMark
              ).length,
        status: !complete
          ? "Incomplete"
          : percentage < this.passMark
          ? "Needs support"
          : "On track",
        previousAverage: source.previousAverage,
      };
    },
    sampleRowsForTerm() {
      return this.mockRecords.filter(
        (row) => row.termId === this.filters.RegisteredTermId
      );
    },
    errorText(error, fallback) {
      return error && error.response && error.response.data
        ? String(error.response.data)
        : error && error.message
        ? error.message
        : fallback;
    },
    async toggleMode() {
      this.requestId += 1;
      this.cascadeId += 1;
      this.gradeScaleRequestId += 1;
      this.loading = false;
      this.listLoading = {
        terms: false,
        classes: false,
        options: false,
        sections: false,
        students: false,
        subjects: false,
        gradeScales: false,
      };
      this.mockMode = !this.mockMode;
      this.hasGenerated = false;
      this.errorMessage = "";
      this.rows = [];
      this.appliedFilters = null;
      this.options = [];
      this.sections = [];
      this.students = [];
      this.subjects = [];
      if (this.mockMode) {
        this.terms = this.mockTerms;
        this.classes = this.mockClasses.map((item) => ({
          Id: item.id,
          Sch_Class: item.name,
        }));
        this.gradeCategories = [
          { Id: "demo-grades", CategoryName: "Demonstration scale" },
        ];
        this.gradeScale = this.mockGrades;
        this.filters = {
          RegisteredTermId: "demo-2025-1",
          ClassId: "",
          OptionId: "",
          SectionId: "",
          StudentId: "",
          AssessmentType: "full",
          SubjectId: "",
          GradeCategoryId: "demo-grades",
        };
        this.appliedFilters = Object.assign({}, this.filters);
        this.rows = this.sampleRowsForTerm()
          .filter((row) => row.assessmentType === this.filters.AssessmentType)
          .map((row) => this.materializeMockRow(row, this.mockSubjects));
        this.hasGenerated = true;
      } else {
        this.filters = {
          RegisteredTermId: "",
          ClassId: "",
          OptionId: "",
          SectionId: "",
          StudentId: "",
          AssessmentType: "full",
          SubjectId: "",
          GradeCategoryId: "",
        };
        this.gradeScale = [];
        await this.loadInitialLists();
      }
    },
    clearFilters() {
      this.filters = Object.assign({}, this.filters, {
        ClassId: "",
        OptionId: "",
        SectionId: "",
        StudentId: "",
        SubjectId: "",
      });
      this.options = [];
      this.sections = [];
      this.students = [];
      this.subjects = [];
      this.rows = [];
      this.hasGenerated = false;
      this.appliedFilters = null;
      this.search = "";
      this.page = 1;
      this.errorMessage = "";
    },
    openStudent(row) {
      this.filters.StudentId = row.id;
      this.appliedFilters = Object.assign({}, this.appliedFilters, {
        StudentId: row.id,
      });
    },
    closeStudent() {
      this.filters.StudentId = "";
      this.appliedFilters = Object.assign({}, this.appliedFilters, {
        StudentId: "",
      });
    },
    exportRanking() {
      const term = this.terms.find(
        (item) =>
          String(item.registeredTermId) ===
          (this.appliedFilters && this.appliedFilters.RegisteredTermId)
      );
      const data = this.filteredRows.map((row) => [
        row.rank || "",
        row.admission,
        row.name,
        row.className,
        row.optionName,
        row.sectionName,
        row.subjectCount,
        row.resultsCount,
        row.totalObtained,
        row.totalMaximum,
        row.complete ? `${row.percentage}%` : "",
        row.grade,
        row.passed,
        row.failed,
        row.status,
      ]);
      const rows = [
        ["Class Performance Ranking"],
        ["Registered term", term ? `${term.schSession} · ${term.schTerm}` : ""],
        ["Scope", this.activeFilters.join(" · ")],
        [
          "Assessment",
          this.appliedFilters && this.appliedFilters.AssessmentType === "mid"
            ? "Mid-term"
            : "Full term",
        ],
        [],
        [
          "Rank",
          "Admission no.",
          "Student name",
          "Class",
          "Option",
          "Section",
          "Subjects offered",
          "With results",
          "Marks obtained",
          "Maximum marks",
          "Overall percentage",
          "Grade",
          "Passed",
          "Failed",
          "Status",
        ],
      ].concat(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(rows),
        "Class Ranking"
      );
      XLSX.writeFile(workbook, "class-performance-ranking.xlsx");
    },
    printRanking() {
      window.print();
    },
  },
  mounted() {
    this.loadInitialLists();
  },
};
</script>

<style scoped>
.ranking-shell {
  padding-bottom: 3rem;
}
.print-context {
  display: none;
}
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}
.page-heading h1 {
  margin: 0.25rem 0;
  color: #17283f;
  font-size: 1.85rem;
  font-weight: 750;
}
.page-heading p,
.section-heading p,
.table-heading p {
  margin: 0;
  color: #78869a;
}
.eyebrow {
  color: #5284c5;
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.heading-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.mode-pill {
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}
.mode-pill i {
  margin-right: 0.4rem;
}
.live-pill {
  color: #26754e;
  background: #e9f6ef;
}
.demo-pill {
  color: #946415;
  background: #fff5df;
}
.filter-panel,
.analysis-card,
.ranking-table,
.student-detail,
.welcome-state {
  border-radius: 14px;
}
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.section-heading h2,
.table-heading h2 {
  margin: 0 0 0.2rem;
  color: #20334d;
  font-size: 1.02rem;
  font-weight: 750;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 0.8rem 1rem;
}
.filter-grid .form-group {
  margin: 0;
}
.filter-grid label {
  display: block;
  margin-bottom: 0.4rem;
  color: #41536a;
  font-size: 0.77rem;
  font-weight: 700;
}
.filter-grid .form-control {
  font-size: 0.84rem;
}
.required {
  color: #d25757;
}
.inline-loader {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: 0.45rem;
  color: #748399;
  font-size: 0.65rem;
  font-weight: 500;
}
.inline-loader i {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid #d9e2ed;
  border-top-color: #4385db;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.55rem;
  padding-top: 1.55rem;
}
.filter-note {
  margin: 0.9rem 0 0;
  color: #78879a;
  font-size: 0.77rem;
}
.active-filters {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;
  color: #687990;
  font-size: 0.76rem;
}
.filter-chip {
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  color: #435772;
  background: #f0f4f9;
}
.demo-banner {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1rem;
  padding: 0.8rem 1rem;
  border: 1px solid #f3dfb2;
  border-radius: 10px;
  color: #77571d;
  background: #fff6e5;
  font-size: 0.83rem;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
}
.kpi-card {
  display: flex;
  min-height: 98px;
  gap: 0.75rem;
  padding: 0.9rem;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(27, 50, 78, 0.07);
}
.kpi-icon {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}
.kpi-card small,
.kpi-note {
  display: block;
  color: #738197;
  font-size: 0.7rem;
}
.kpi-card small {
  font-weight: 650;
}
.kpi-card strong {
  display: block;
  overflow: hidden;
  color: #22354e;
  font-size: 1.13rem;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kpi-note {
  margin-top: 0.1rem;
  color: #8a96a6;
  font-size: 0.65rem;
}
.tone-blue .kpi-icon {
  color: #427cdb;
  background: #eaf2ff;
}
.tone-violet .kpi-icon {
  color: #795bd1;
  background: #f0ecff;
}
.tone-slate .kpi-icon {
  color: #61738b;
  background: #edf1f5;
}
.tone-green .kpi-icon {
  color: #30865d;
  background: #e7f6ee;
}
.tone-red .kpi-icon {
  color: #cb6260;
  background: #fff0ef;
}
.tone-amber .kpi-icon {
  color: #bd8320;
  background: #fff5e3;
}
.tone-teal .kpi-icon {
  color: #25877d;
  background: #e6f7f5;
}
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.analysis-card .card-body {
  padding: 1.2rem;
}
.distribution-bars,
.subject-bars,
.top-list {
  display: grid;
  gap: 0.8rem;
}
.distribution-row {
  display: grid;
  grid-template-columns: minmax(100px, 1fr) 2fr 28px;
  align-items: center;
  gap: 0.7rem;
  color: #56677e;
  font-size: 0.75rem;
}
.distribution-row strong {
  text-align: right;
  color: #334760;
}
.distribution-track,
.subject-track,
.mini-track {
  height: 9px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf1f6;
}
.distribution-track i,
.subject-track i,
.mini-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
}
.subject-row {
  display: grid;
  grid-template-columns: minmax(100px, 1fr) 2fr 40px 85px;
  align-items: center;
  gap: 0.6rem;
  color: #56677e;
  font-size: 0.73rem;
}
.subject-row > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.subject-row strong {
  text-align: right;
  color: #334760;
}
.subject-row small {
  color: #8995a5;
  text-align: right;
  font-size: 0.65rem;
}
.subject-track i {
  background: #4b89e9;
}
.subject-track i.weak-subject {
  background: #df7770;
}
.comparison-list {
  display: grid;
  gap: 0.85rem;
}
.comparison-row {
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 2fr 42px;
  align-items: center;
  gap: 0.7rem;
  color: #56677e;
  font-size: 0.74rem;
}
.comparison-row > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.comparison-row strong {
  color: #334760;
  text-align: right;
}
.comparison-row .subject-track i {
  background: #55a17b;
}
.top-row {
  display: grid;
  grid-template-columns: 28px minmax(90px, 1fr) minmax(50px, 0.8fr) 43px;
  align-items: center;
  gap: 0.55rem;
}
.rank-medal,
.rank-badge {
  display: inline-grid;
  width: 25px;
  height: 25px;
  place-items: center;
  border-radius: 50%;
  color: #526881;
  background: #eef2f6;
  font-size: 0.7rem;
  font-weight: 800;
}
.top-student {
  min-width: 0;
}
.top-student strong,
.top-student small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.top-student strong {
  color: #344a63;
  font-size: 0.76rem;
}
.top-student small {
  color: #8b97a7;
  font-size: 0.65rem;
}
.mini-track i {
  background: #54a47b;
}
.top-row > b {
  color: #344a63;
  text-align: right;
  font-size: 0.75rem;
}
.grade-layout {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  gap: 1.3rem;
}
.grade-donut {
  position: relative;
  display: grid;
  width: 145px;
  height: 145px;
  flex: none;
  place-items: center;
  border-radius: 50%;
}
.grade-donut:before {
  position: absolute;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: white;
  content: "";
}
.grade-donut > div {
  position: relative;
  text-align: center;
}
.grade-donut strong,
.grade-donut small {
  display: block;
}
.grade-donut strong {
  color: #263b56;
  font-size: 1.2rem;
}
.grade-donut small {
  color: #8794a6;
  font-size: 0.68rem;
}
.grade-legend {
  display: grid;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.grade-legend li {
  display: flex;
  gap: 0.4rem;
  color: #53637a;
  font-size: 0.73rem;
}
.grade-legend i {
  width: 9px;
  height: 9px;
  flex: none;
  margin-top: 0.25rem;
  border-radius: 50%;
}
.grade-legend small {
  display: block;
  color: #8995a5;
  font-size: 0.66rem;
}
.pass-summary {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  gap: 1.4rem;
}
.pass-ring {
  display: grid;
  width: 125px;
  height: 125px;
  flex: none;
  place-items: center;
  border-radius: 50%;
  position: relative;
}
.pass-ring:before {
  position: absolute;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: white;
  content: "";
}
.pass-ring strong {
  position: relative;
  color: #30445e;
  font-size: 1.15rem;
}
.pass-stats {
  display: grid;
  gap: 0.65rem;
  color: #596b81;
  font-size: 0.75rem;
}
.pass-stats span {
  display: grid;
  grid-template-columns: 10px 1fr 25px;
  align-items: center;
  gap: 0.4rem;
}
.pass-stats b {
  text-align: right;
  color: #30445e;
}
.pass-stats i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.pass-dot {
  background: #338866;
}
.fail-dot {
  background: #e46b6b;
}
.support-dot {
  background: #dfa83c;
}
.trend-wrap {
  height: 175px;
}
.trend-wrap svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.chart-gridline {
  stroke: #edf1f6;
  stroke-width: 1;
}
.chart-axis {
  fill: #8492a6;
  font-size: 10px;
}
.chart-empty,
.empty-state {
  display: grid;
  min-height: 150px;
  place-items: center;
  text-align: center;
  color: #929eae;
  font-size: 0.82rem;
}
.student-detail {
  margin-top: 1rem;
}
.detail-stats {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.7rem;
  padding: 1rem;
  border-radius: 9px;
  background: #f7f9fc;
}
.detail-stats small,
.detail-stats strong {
  display: block;
}
.detail-stats small {
  color: #7e8b9d;
  font-size: 0.68rem;
}
.detail-stats strong {
  color: #304761;
  font-size: 0.9rem;
}
.subject-detail-table {
  margin-top: 1rem;
}
.subject-detail-table h3 {
  color: #354b65;
  font-size: 0.86rem;
}
.subject-detail-table th {
  color: #77869a;
  font-size: 0.68rem;
  text-transform: uppercase;
}
.subject-detail-table td {
  color: #4d6078;
  font-size: 0.75rem;
}
.table-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.9rem;
}
.table-heading p {
  font-size: 0.75rem;
}
.table-tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.search-box {
  display: flex;
  height: 34px;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  padding: 0 0.6rem;
  border: 1px solid #dce3ec;
  border-radius: 6px;
  color: #8794a6;
}
.search-box input {
  width: 190px;
  border: 0;
  outline: 0;
  font-size: 0.76rem;
}
.ranking-data th {
  padding: 0.75rem 0.6rem;
  color: #738198;
  background: #f8fafc;
  font-size: 0.66rem;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  white-space: nowrap;
}
.ranking-data td {
  padding: 0.65rem 0.6rem;
  color: #43556c;
  font-size: 0.75rem;
  vertical-align: middle;
  white-space: nowrap;
}
.ranking-data th.sortable {
  cursor: pointer;
}
.ranking-data td strong {
  color: #324862;
}
.ranking-data td small {
  display: block;
}
.top-row-highlight {
  background: #fbfdfb;
}
.incomplete-row {
  background: #fffdf7;
}
.support-row {
  background: #fffafa;
}
.rank-first {
  color: #8c691d;
  background: #fff0c4;
}
.rank-second {
  color: #68778a;
  background: #edf0f4;
}
.rank-third {
  color: #966949;
  background: #f7eadd;
}
.unranked {
  color: #a4adba;
}
.incomplete-note {
  color: #ac7a20;
  font-size: 0.63rem;
}
.status-badge {
  display: inline-block;
  padding: 0.24rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 750;
}
.status-good {
  color: #23714c;
  background: #e7f5ec;
}
.status-risk {
  color: #aa4b4b;
  background: #ffebea;
}
.status-incomplete {
  color: #926a1d;
  background: #fff4db;
}
.empty-state {
  padding: 2.5rem 1rem;
}
.empty-state > i {
  color: #90a0b4;
  font-size: 1.5rem;
}
.empty-state h3 {
  margin: 0.6rem 0 0.2rem;
  color: #354b65;
  font-size: 1rem;
}
.empty-state p {
  margin: 0;
  font-size: 0.8rem;
}
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 1.25rem;
  border-top: 1px solid #edf0f5;
  color: #7e8b9d;
  font-size: 0.74rem;
}
.page-controls {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.page-controls label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
}
.page-controls select {
  width: auto;
}
.report-footnote {
  margin: 0.8rem 0.2rem;
  color: #8793a3;
  font-size: 0.7rem;
}
.welcome-state {
  padding: 3rem 1rem;
  text-align: center;
}
.welcome-state > span {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  margin: 0 auto 0.8rem;
  border-radius: 50%;
  color: #8092a8;
  background: #f0f4f8;
}
.welcome-state h2 {
  color: #354b65;
  font-size: 1.05rem;
}
.welcome-state p {
  color: #8290a2;
  font-size: 0.82rem;
}
@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .filter-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }
  .detail-stats {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 767px) {
  .analysis-grid {
    grid-template-columns: 1fr;
  }
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .table-heading {
    align-items: flex-start;
  }
  .table-tools {
    width: 100%;
  }
  .search-box {
    flex: 1;
  }
  .search-box input {
    width: 100%;
    min-width: 100px;
  }
  .subject-row {
    grid-template-columns: minmax(90px, 1fr) 1.4fr 38px;
  }
  .subject-row small {
    display: none;
  }
}
@media (max-width: 480px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    padding-top: 0.2rem;
  }
  .filter-actions .btn {
    flex: 1;
  }
  .kpi-card {
    min-height: 90px;
    padding: 0.7rem;
  }
  .kpi-card strong {
    font-size: 1rem;
  }
  .kpi-icon {
    width: 31px;
    height: 31px;
  }
  .detail-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .grade-layout,
  .pass-summary {
    gap: 0.7rem;
  }
  .grade-donut {
    width: 120px;
    height: 120px;
  }
  .grade-donut:before {
    width: 74px;
    height: 74px;
  }
  .pagination-row {
    align-items: flex-start;
    flex-direction: column;
  }
  .heading-actions {
    width: 100%;
    justify-content: space-between;
  }
}
@media print {
  .print-context {
    display: block;
    margin: 0.5rem 0;
    color: #20334d;
    font-size: 1rem;
    font-weight: 700;
  }
  .heading-actions,
  .filter-panel,
  .table-tools,
  .pagination-row {
    display: none !important;
  }
  .ranking-shell {
    margin: 0 !important;
    padding: 0 !important;
  }
  .kpi-card,
  .analysis-card,
  .ranking-table,
  .student-detail {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
}
</style>

<style>
@media print {
  @page {
    margin: 12mm;
  }
  body * {
    visibility: hidden !important;
  }
  .ranking-report,
  .ranking-report * {
    visibility: visible !important;
  }
  .ranking-report {
    position: fixed !important;
    inset: 0 auto auto 0 !important;
    z-index: 999999 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
  .ranking-report > .header {
    display: none !important;
  }
  .ranking-report .ranking-shell {
    position: static !important;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
}
</style>
